import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Trash2, RefreshCw, Sparkles, Upload, Loader2 } from "lucide-react";

interface KnowledgeEntry {
  id: string;
  category: string;
  title: string;
  content: string;
  link: string | null;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

interface ParsedEntry {
  category: string;
  title: string;
  content: string;
  selected: boolean;
}

const CATEGORIES = [
  "FAQ",
  "Services",
  "Pricing",
  "Policies",
  "About",
  "General",
];

export function AIKnowledgeManager() {
  const [entries, setEntries] = useState<KnowledgeEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [importText, setImportText] = useState("");
  const [parsedEntries, setParsedEntries] = useState<ParsedEntry[]>([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [editingEntry, setEditingEntry] = useState<KnowledgeEntry | null>(null);
  const [formData, setFormData] = useState({
    category: "FAQ",
    title: "",
    content: "",
    link: "",
    is_active: true,
    sort_order: 0,
  });
  const { toast } = useToast();

  useEffect(() => {
    fetchEntries();
  }, []);

  const fetchEntries = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from("ai_knowledge")
        .select("*")
        .order("category")
        .order("sort_order");

      if (error) throw error;
      setEntries(data || []);
    } catch (error: any) {
      toast({
        title: "Error loading knowledge entries",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (editingEntry) {
        const { error } = await supabase
          .from("ai_knowledge")
          .update(formData)
          .eq("id", editingEntry.id);

        if (error) throw error;
        toast({ title: "Entry updated" });
      } else {
        const { error } = await supabase
          .from("ai_knowledge")
          .insert(formData);

        if (error) throw error;
        toast({ title: "Entry created" });
      }

      setIsDialogOpen(false);
      resetForm();
      fetchEntries();
    } catch (error: any) {
      toast({
        title: "Error saving entry",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleEdit = (entry: KnowledgeEntry) => {
    setEditingEntry(entry);
    setFormData({
      category: entry.category,
      title: entry.title,
      content: entry.content,
      link: entry.link || "",
      is_active: entry.is_active,
      sort_order: entry.sort_order,
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this entry?")) return;

    try {
      const { error } = await supabase
        .from("ai_knowledge")
        .delete()
        .eq("id", id);

      if (error) throw error;
      toast({ title: "Entry deleted" });
      fetchEntries();
    } catch (error: any) {
      toast({
        title: "Error deleting entry",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const toggleActive = async (id: string, is_active: boolean) => {
    try {
      const { error } = await supabase
        .from("ai_knowledge")
        .update({ is_active })
        .eq("id", id);

      if (error) throw error;
      setEntries((prev) =>
        prev.map((e) => (e.id === id ? { ...e, is_active } : e))
      );
    } catch (error: any) {
      toast({
        title: "Error updating status",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const resetForm = () => {
    setEditingEntry(null);
    setFormData({
      category: "FAQ",
      title: "",
      content: "",
      link: "",
      is_active: true,
      sort_order: 0,
    });
  };

  const analyzeText = async () => {
    if (!importText.trim()) {
      toast({ title: "Please enter some text to analyze", variant: "destructive" });
      return;
    }

    setIsAnalyzing(true);
    try {
      const { data, error } = await supabase.functions.invoke("ai-content", {
        body: { type: "parse-knowledge", text: importText },
      });

      if (error) throw error;

      const extractedEntries = data.result?.entries || [];
      setParsedEntries(
        extractedEntries.map((entry: any) => ({
          ...entry,
          selected: true,
        }))
      );

      if (extractedEntries.length === 0) {
        toast({ title: "No knowledge entries could be extracted", variant: "destructive" });
      } else {
        toast({ title: `Found ${extractedEntries.length} knowledge entries` });
      }
    } catch (error: any) {
      toast({
        title: "Error analyzing text",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsAnalyzing(false);
    }
  };

  const toggleEntrySelection = (index: number) => {
    setParsedEntries((prev) =>
      prev.map((entry, i) =>
        i === index ? { ...entry, selected: !entry.selected } : entry
      )
    );
  };

  const selectAllEntries = (selected: boolean) => {
    setParsedEntries((prev) => prev.map((entry) => ({ ...entry, selected })));
  };

  const updateParsedEntry = (index: number, field: keyof ParsedEntry, value: string) => {
    setParsedEntries((prev) =>
      prev.map((entry, i) =>
        i === index ? { ...entry, [field]: value } : entry
      )
    );
  };

  const importSelectedEntries = async () => {
    const selectedEntries = parsedEntries.filter((e) => e.selected);
    if (selectedEntries.length === 0) {
      toast({ title: "No entries selected", variant: "destructive" });
      return;
    }

    setIsImporting(true);
    try {
      const entriesToInsert = selectedEntries.map((entry, index) => ({
        category: entry.category,
        title: entry.title,
        content: entry.content,
        is_active: true,
        sort_order: index,
      }));

      const { error } = await supabase.from("ai_knowledge").insert(entriesToInsert);

      if (error) throw error;

      toast({ title: `Imported ${selectedEntries.length} entries` });
      setIsImportDialogOpen(false);
      setImportText("");
      setParsedEntries([]);
      fetchEntries();
    } catch (error: any) {
      toast({
        title: "Error importing entries",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsImporting(false);
    }
  };

  const seedFromFAQ = async () => {
    const faqData = [
      {
        category: "FAQ",
        title: "How far in advance should I book?",
        content: "We recommend booking 12-18 months before your wedding date, especially for peak season (May-October). However, we sometimes have last-minute availability, so don't hesitate to reach out!",
        is_active: true,
        sort_order: 1,
      },
      {
        category: "FAQ",
        title: "What's your payment structure?",
        content: "We require a 30% non-refundable retainer to secure your date, with the remaining balance due 2 weeks before your wedding. We accept all major credit cards, bank transfers, and checks.",
        is_active: true,
        sort_order: 2,
      },
      {
        category: "FAQ",
        title: "How long until we receive our photos/videos?",
        content: "Wedding photo galleries are delivered within 6-8 weeks. Highlight films are delivered within 8-10 weeks, and full feature films within 12-16 weeks. Rush delivery is available for an additional fee.",
        is_active: true,
        sort_order: 3,
      },
      {
        category: "FAQ",
        title: "Do you travel for destination weddings?",
        content: "Absolutely! We love destination weddings. Travel fees apply for weddings outside of our local area. Contact us for a custom quote for your destination.",
        is_active: true,
        sort_order: 4,
      },
      {
        category: "About",
        title: "About Pixie Clicks",
        content: "Pixie Clicks is a premier wedding photography and videography studio specializing in capturing authentic moments with an artistic eye. We believe every love story deserves to be told beautifully, and we're passionate about creating timeless imagery that you'll treasure forever.",
        is_active: true,
        sort_order: 1,
      },
      {
        category: "Services",
        title: "Photography Services",
        content: "Our photography packages include full-day coverage, a second photographer, online gallery with high-resolution downloads, professional editing, and print rights. We specialize in candid moments, editorial portraits, and documentary-style coverage.",
        is_active: true,
        sort_order: 1,
      },
      {
        category: "Services",
        title: "Cinematography Services",
        content: "Our videography packages include full-day coverage, highlight films, documentary edits, and raw footage options. We use cinema-quality equipment and professional audio capture for the most important moments.",
        is_active: true,
        sort_order: 2,
      },
      {
        category: "Policies",
        title: "Cancellation Policy",
        content: "The 30% retainer is non-refundable as it secures your date exclusively. If you need to postpone, we're happy to transfer your contract to a new date within 18 months, subject to availability.",
        is_active: true,
        sort_order: 1,
      },
    ];

    try {
      const { error } = await supabase.from("ai_knowledge").insert(faqData);

      if (error) throw error;
      toast({ title: "Knowledge base seeded with sample content" });
      fetchEntries();
    } catch (error: any) {
      toast({
        title: "Error seeding data",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground">
            AI Knowledge Base
          </h2>
          <p className="text-sm text-muted-foreground">
            Manage information the AI chatbot uses to answer questions
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={fetchEntries} disabled={isLoading}>
            <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          {entries.length === 0 && (
            <Button variant="outline" onClick={seedFromFAQ}>
              <Sparkles className="mr-2 h-4 w-4" />
              Seed Sample Data
            </Button>
          )}
          
          {/* Import Text Dialog */}
          <Dialog open={isImportDialogOpen} onOpenChange={(open) => {
            setIsImportDialogOpen(open);
            if (!open) {
              setImportText("");
              setParsedEntries([]);
            }
          }}>
            <DialogTrigger asChild>
              <Button variant="outline">
                <Upload className="mr-2 h-4 w-4" />
                Import Text
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
              <DialogHeader>
                <DialogTitle>Import Text with AI</DialogTitle>
              </DialogHeader>
              
              {parsedEntries.length === 0 ? (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Paste your text content</Label>
                    <Textarea
                      value={importText}
                      onChange={(e) => setImportText(e.target.value)}
                      placeholder="Paste FAQ content, website text, documents, or any text you want to convert into knowledge entries..."
                      rows={12}
                      className="resize-none"
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="outline"
                      onClick={() => setIsImportDialogOpen(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={analyzeText}
                      disabled={isAnalyzing || !importText.trim()}
                    >
                      {isAnalyzing ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Analyzing...
                        </>
                      ) : (
                        <>
                          <Sparkles className="mr-2 h-4 w-4" />
                          Analyze with AI
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col gap-4 flex-1 min-h-0">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-muted-foreground">
                      {parsedEntries.filter((e) => e.selected).length} of {parsedEntries.length} entries selected
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => selectAllEntries(true)}
                      >
                        Select All
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => selectAllEntries(false)}
                      >
                        Deselect All
                      </Button>
                    </div>
                  </div>
                  
                  <ScrollArea className="flex-1 border rounded-lg">
                    <div className="p-4 space-y-4">
                      {parsedEntries.map((entry, index) => (
                        <div
                          key={index}
                          className={`p-4 rounded-lg border ${
                            entry.selected ? "bg-accent/50 border-primary/30" : "bg-muted/30"
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <Checkbox
                              checked={entry.selected}
                              onCheckedChange={() => toggleEntrySelection(index)}
                              className="mt-1"
                            />
                            <div className="flex-1 space-y-3">
                              <div className="flex gap-2 items-center">
                                <Select
                                  value={entry.category}
                                  onValueChange={(value) =>
                                    updateParsedEntry(index, "category", value)
                                  }
                                >
                                  <SelectTrigger className="w-32">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {CATEGORIES.map((cat) => (
                                      <SelectItem key={cat} value={cat}>
                                        {cat}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <Input
                                value={entry.title}
                                onChange={(e) =>
                                  updateParsedEntry(index, "title", e.target.value)
                                }
                                placeholder="Title"
                                className="font-medium"
                              />
                              <Textarea
                                value={entry.content}
                                onChange={(e) =>
                                  updateParsedEntry(index, "content", e.target.value)
                                }
                                placeholder="Content"
                                rows={3}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                  
                  <div className="flex justify-between gap-2 pt-2">
                    <Button
                      variant="outline"
                      onClick={() => setParsedEntries([])}
                    >
                      Back to Text
                    </Button>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        onClick={() => setIsImportDialogOpen(false)}
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={importSelectedEntries}
                        disabled={isImporting || parsedEntries.filter((e) => e.selected).length === 0}
                      >
                        {isImporting ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Importing...
                          </>
                        ) : (
                          <>
                            <Plus className="mr-2 h-4 w-4" />
                            Import {parsedEntries.filter((e) => e.selected).length} Entries
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* Add Entry Dialog */}
          <Dialog open={isDialogOpen} onOpenChange={(open) => {
            setIsDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                Add Entry
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>
                  {editingEntry ? "Edit Knowledge Entry" : "Add Knowledge Entry"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="category">Category</Label>
                    <Select
                      value={formData.category}
                      onValueChange={(value) =>
                        setFormData((prev) => ({ ...prev, category: value }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map((cat) => (
                          <SelectItem key={cat} value={cat}>
                            {cat}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="sort_order">Sort Order</Label>
                    <Input
                      id="sort_order"
                      type="number"
                      value={formData.sort_order}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          sort_order: parseInt(e.target.value) || 0,
                        }))
                      }
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="title">Title / Question</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, title: e.target.value }))
                    }
                    placeholder="e.g., How far in advance should I book?"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="content">Content / Answer</Label>
                  <Textarea
                    id="content"
                    value={formData.content}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, content: e.target.value }))
                    }
                    placeholder="The detailed answer or information..."
                    rows={6}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="link">Related Page Link (optional)</Label>
                  <Input
                    id="link"
                    value={formData.link}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, link: e.target.value }))
                    }
                    placeholder="e.g., /packages or /photography-by-pixie-clicks"
                  />
                  <p className="text-xs text-muted-foreground">
                    Add a page path to link users to when answering this question
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    id="is_active"
                    checked={formData.is_active}
                    onCheckedChange={(checked) =>
                      setFormData((prev) => ({ ...prev, is_active: checked }))
                    }
                  />
                  <Label htmlFor="is_active">Active (include in AI context)</Label>
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button type="submit">
                    {editingEntry ? "Update" : "Create"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-xl bg-card border border-border overflow-hidden">
        {isLoading ? (
          <div className="flex h-64 items-center justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          </div>
        ) : entries.length === 0 ? (
          <div className="flex h-64 flex-col items-center justify-center gap-4 text-muted-foreground">
            <p>No knowledge entries yet</p>
            <Button variant="outline" onClick={seedFromFAQ}>
              <Sparkles className="mr-2 h-4 w-4" />
              Seed with Sample Data
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">Active</TableHead>
                  <TableHead className="w-24">Category</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead className="hidden md:table-cell">Content Preview</TableHead>
                  <TableHead className="w-24">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell>
                      <Switch
                        checked={entry.is_active}
                        onCheckedChange={(checked) =>
                          toggleActive(entry.id, checked)
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">{entry.category}</Badge>
                    </TableCell>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {entry.title}
                        {entry.link && (
                          <Badge variant="outline" className="text-xs">
                            {entry.link}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell max-w-xs truncate text-muted-foreground">
                      {entry.content}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(entry)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(entry.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
